// src/pages/SearchResultsPage.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ProductCard from '../components/ProductCard';
import { Search, Sparkles, Filter, SortAsc, Loader2 } from 'lucide-react';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { 
    searchResults, 
    searchLoading, 
    searchIntent, 
    searchSuggestions, 
    lastSearchQuery,
    performAISearch 
  } = useApp();

  const [sortBy, setSortBy] = useState('relevance');
  const [filterBy, setFilterBy] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);

  const query = searchParams.get('q') || lastSearchQuery;

  useEffect(() => {
    if (query && query !== lastSearchQuery) {
      try {
        performAISearch(query);
        setError(null);
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to perform search');
      }
    }
  }, [query, performAISearch, lastSearchQuery]);

  const handleSort = (sortType) => {
    setSortBy(sortType);
  };

  const handleFilter = (filterType) => {
    setFilterBy(filterType);
  };

  const getSortedAndFilteredResults = () => {
    let results = [...searchResults];

    // 필터링
    if (filterBy !== 'all') {
      results = results.filter(product => product.category === filterBy);
    }

    // 정렬
    switch (sortBy) {
      case 'price-low':
        results.sort((a, b) => (a.moqPrices?.[0]?.price || 0) - (b.moqPrices?.[0]?.price || 0));
        break;
      case 'price-high':
        results.sort((a, b) => (b.moqPrices?.[0]?.price || 0) - (a.moqPrices?.[0]?.price || 0));
        break;
      case 'newest':
        results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        break;
      case 'popular':
        results.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
        break;
      case 'relevance':
      default:
        results.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
        break;
    }

    return results;
  };

  const sortedResults = getSortedAndFilteredResults();
  const categories = [...new Set(searchResults.map(p => p.category).filter(Boolean))];

  if (searchLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-blue-600" size={32} />
          <span className="ml-3 text-lg text-gray-600">Searching with AI...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-16">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Search Error</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* 검색 헤더 */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Search className="text-gray-400" size={24} />
          <h1 className="text-2xl font-bold text-gray-900">
            Search Results for "{query}"
          </h1>
        </div>

        {/* AI 검색 의도 */}
        {searchIntent && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <Sparkles className="text-blue-600" size={16} />
            <span className="text-sm font-medium text-blue-900">AI Search Intent:</span>
            <span className="text-sm text-blue-700">{searchIntent}</span>
          </div>
        )}

        {/* 검색 제안 */}
        {searchSuggestions.length > 0 && (
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">Did you mean:</p>
            <div className="flex flex-wrap gap-2">
              {searchSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
                  }}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 결과 통계 및 컨트롤 */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600">
              {sortedResults.length} product{sortedResults.length !== 1 ? 's' : ''} found
            </p>
            {searchResults.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Avg. relevance:</span>
                <span className="font-medium text-green-600">
                  {Math.round(
                    searchResults.reduce((sum, p) => sum + (p.relevanceScore || 0), 0) / searchResults.length * 100
                  )}%
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* 필터 버튼 */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter size={16} />
              Filter
            </button>

            {/* 정렬 드롭다운 */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => handleSort(e.target.value)}
                className="appearance-none px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="relevance">Most Relevant</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest First</option>
                <option value="popular">Most Popular</option>
              </select>
              <SortAsc className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            </div>
          </div>
        </div>

        {/* 필터 패널 */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleFilter('all')}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  filterBy === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                All Categories
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => handleFilter(category)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterBy === category 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 검색 결과 */}
      {sortedResults.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedResults.map((product) => (
            <div key={product.id} className="relative">
              <ProductCard product={product} />
              
              {/* AI 매칭 정보 */}
              {product.relevanceScore && (
                <div className="absolute top-2 right-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                  {Math.round(product.relevanceScore * 100)}% match
                </div>
              )}
              
              {product.matchReason && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  <span className="font-medium">Why this matches:</span> {product.matchReason}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Search className="mx-auto text-gray-300 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-500 mb-6">
            We couldn't find any products matching "{query}". Try different keywords or browse our categories.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/products')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse All Products
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      )}

      {/* AI 추천 전략 */}
      {searchResults.length > 0 && (
        <div className="mt-12 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
          <div className="flex items-start gap-3">
            <Sparkles className="text-blue-600 mt-1" size={20} />
            <div>
              <h3 className="font-medium text-blue-900 mb-2">AI Search Strategy</h3>
              <p className="text-sm text-blue-700">
                Our AI analyzed your search query and found products based on semantic similarity, 
                category matching, and user behavior patterns. The results are ranked by relevance 
                to help you find exactly what you're looking for.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;
